import { useCallback, useEffect, useMemo, useState } from "react";
import {
  NonCancelableCustomEvent,
  TableProps,
} from "@amzn/awsui-components-react";
import { DLQItem } from "../lusca-search-api/generated-src";

/**
 * "returned": only items the Lusca redriver requeued that then failed again and came back to
 * the DLQ (redrive_attempted === true). "not_returned": everything else, including items that
 * have never been redriven. Absence from the DLQ is not proof of downstream success.
 */
export type RedriveLineageFilter = "all" | "returned" | "not_returned";

export const matchesRedriveLineageFilter = (
  item: DLQItem,
  filter: RedriveLineageFilter
): boolean => {
  if (filter === "all") return true;
  const returned = item.redrive_attempted === true;
  return filter === "returned" ? returned : !returned;
};

export function useDlqTable(dlqItems: DLQItem[]) {
  const [selectedItem, setSelectedItem] = useState<DLQItem | null>(null);
  const [sortingColumn, setSortingColumn] = useState<
    TableProps.SortingColumn<DLQItem>
  >({
    sortingField: "timestamp",
  });
  const [sortingDescending, setSortingDescending] = useState(true);
  const [filteringText, setFilteringText] = useState("");
  const [redriveFilter, setRedriveFilter] =
    useState<RedriveLineageFilter>("all");
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize] = useState(20);

  // A narrower filter can leave the current page beyond the last one, which would render an
  // empty table even though items match; start from the first page whenever a filter changes.
  useEffect(() => {
    setCurrentPageIndex(1);
  }, [filteringText, redriveFilter]);

  const queueSummary = useMemo(() => {
    const summary: { [key: string]: number } = {};
    dlqItems.forEach((item) => {
      summary[item.eventsourcekey] = (summary[item.eventsourcekey] || 0) + 1;
    });
    return summary;
  }, [dlqItems]);

  const filteredItems = useMemo(
    () =>
      dlqItems.filter((item) => {
        if (!matchesRedriveLineageFilter(item, redriveFilter)) return false;
        const searchText = filteringText.toLowerCase();
        const messageString = item.is_malformed_json
          ? (item.processed_body as string)
          : JSON.stringify(item.processed_body);

        return (
          Object.entries(item)
            .filter(([key]) => key !== "message")
            .some(([, value]) =>
              String(value).toLowerCase().includes(searchText)
            ) || messageString.toLowerCase().includes(searchText)
        );
      }),
    [dlqItems, filteringText, redriveFilter]
  );

  const sortedItems = useMemo(
    () =>
      [...filteredItems].sort((a, b) => {
        const key = sortingColumn.sortingField as keyof DLQItem;
        // Treat null and undefined alike: both sort last.
        const aVal = a[key] ?? undefined;
        const bVal = b[key] ?? undefined;
        // undefined last
        if (aVal === undefined && bVal === undefined) return 0;
        if (aVal === undefined) return 1;
        if (bVal === undefined) return -1;
        if (aVal < bVal) return sortingDescending ? 1 : -1;
        if (aVal > bVal) return sortingDescending ? -1 : 1;
        return 0;
      }),
    [filteredItems, sortingColumn, sortingDescending]
  );

  const paginatedItems = useMemo(
    () =>
      sortedItems.slice(
        (currentPageIndex - 1) * pageSize,
        currentPageIndex * pageSize
      ),
    [sortedItems, currentPageIndex, pageSize]
  );

  const handleSortingChange = useCallback(
    (event: NonCancelableCustomEvent<TableProps.SortingState<DLQItem>>) => {
      setSortingColumn(event.detail.sortingColumn);
      setSortingDescending(event.detail.isDescending ?? false);
    },
    []
  );

  return {
    selectedItem,
    setSelectedItem,
    sortingColumn,
    sortingDescending,
    filteringText,
    setFilteringText,
    redriveFilter,
    setRedriveFilter,
    currentPageIndex,
    setCurrentPageIndex,
    pageSize,
    queueSummary,
    filteredItems,
    paginatedItems,
    handleSortingChange,
  };
}
